/**
 * Client-side presigned upload utility.
 *
 * Uploads images directly to R2 via presigned PUT URLs,
 * keeping image data off the server entirely.
 */

import * as Sentry from "@sentry/react";
import { apiPost } from "./apiClient";
import { fetchWithNetworkRetry } from "./fetchWithRetry";
import { base64ToBlob } from "../base64";
import type { UploadUrlResponse } from "@shared/schemas/api";

// `base64ToBlob` moved to a dependency-free module (`src/lib/base64.ts`) so
// lightweight code can use it without the API/Supabase import graph. Re-export
// keeps existing `from "./uploadImage"` imports working.
export { base64ToBlob };

interface UploadedImage {
  publicUrl: string;
}

/**
 * Request presigned URLs from the server, then upload files directly to R2.
 * Returns the public CDN URLs for each uploaded file.
 */
export async function uploadImagesToR2(
  blobs: Blob[],
): Promise<UploadedImage[]> {
  if (blobs.length === 0) return [];

  const { urls } = await apiPost<UploadUrlResponse>("/api/upload-url", {
    count: blobs.length,
  });

  const results = await Promise.all(
    blobs.map(async (blob, i) => {
      const { presignedUrl, publicUrl } = urls[i];
      const response = await fetchWithNetworkRetry(presignedUrl, {
        method: "PUT",
        headers: { "Content-Type": "image/png" },
        body: blob,
      });
      if (!response.ok) {
        const error = new Error(`Upload failed: ${response.status}`);
        Sentry.withScope((scope) => {
          scope.setTag("type", "upload_failure");
          scope.setExtra("httpStatus", response.status);
          scope.setExtra("fileSize", blob.size);
          Sentry.captureException(error);
        });
        throw error;
      }
      return { publicUrl };
    })
  );

  return results;
}

/**
 * Upload a single image (base64 data URI or Blob) to R2.
 * Returns the public CDN URL.
 */
export async function uploadSingleImageToR2(
  input: string | Blob,
): Promise<string> {
  const blob = typeof input === "string" ? base64ToBlob(input) : input;
  const [result] = await uploadImagesToR2([blob]);
  return result.publicUrl;
}
